import { useCreateLicenseMutation } from "Api/Mutations/Licenses/useCreateLicenseMutation";
import { useLicenseQuery } from "Api/Queries/Licenses/useLicenseQuery";
import { LicenseOrderForm } from "Components/Licenses/LicenseOrderForm/LicenseOrderForm";
import { LicenseOrderFormSkeleton } from "Components/Licenses/LicenseOrderForm/LicenseOrderFormSkeleton";
import { LIFETIME_PERIODICITY } from "Components/Licenses/LicenseOrderForm/constants";
import type { LicenseSelectionModel } from "Components/Licenses/LicenseOrderForm/schema";
import { isCouponError } from "Components/Licenses/CouponInput/couponErrors";
import { getNextAvailableLicense } from "Components/Licenses/licenseUtils";
import { useEnsureUserRegion } from "Hooks/useEnsureUserRegion";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { OwnershipType } from "Infrastructure/Api/Api";
import { useSearchParams } from "Navigation/useSearchParams";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

const RESOURCES = Resources.Components.LicenseOrder;

export const LicenseOrder = () => {
  const [, setSearchParams] = useSearchParams();
  const { t } = useResource();
  // The user's chosen market drives both the offered licenses and the preselected region —
  // never a CZ constant. A region-less user (fresh sign-up before the market choice) gets the
  // form with an empty selector and picks first; the form reloads the offer on selection.
  const userRegion = useLoggedInUser()?.region ?? undefined;
  const { ensureUserRegion, isSettingRegion } = useEnsureUserRegion();
  const {
    data: { items: licenses } = {},
    isLoading,
    isError,
  } = useLicenseQuery(userRegion);

  const { mutate: createLicense, isPending: isCreateLicensePending } =
    useCreateLicenseMutation({
      onSuccess: ({ transaction }) => {
        setSearchParams({ transaction: transaction.publicID });
        toast.success(t(RESOURCES.CreateSuccess));
      },
      onError: (error) => {
        const couponError = isCouponError(error);
        if (couponError) {
          toast.error(t(RESOURCES.Coupon.Error[couponError]));
        } else {
          toast.error(t(RESOURCES.CreateError));
        }
      },
    });

  if (isLoading) {
    return <LicenseOrderFormSkeleton />;
  }

  if (isError || (userRegion && !licenses)) {
    return <ErrorText>{t(RESOURCES.LoadingError)}</ErrorText>;
  }

  const defaultLicense = licenses
    ? getNextAvailableLicense(licenses)
    : undefined;

  const defaultValues: Partial<LicenseSelectionModel> = {
    code: defaultLicense?.code,
    region: userRegion,
  };

  const onSubmit = (data: LicenseSelectionModel) => {
    const handleCreateLicense = () =>
      createLicense({
        region: data.region,
        code: data.code,
        walletAddress: data.walletAddress || "",
        ownershipType: data.isUsingPrivateWallet
          ? OwnershipType.NonCustodial
          : OwnershipType.Custodial,
        subscriptionPeriodicity: LIFETIME_PERIODICITY,
        shouldReplaceUnpaidLicenses: false,
        couponCode: data.couponCode ?? null,
      });
    const handleRegionError = () =>
      toast.error(t(Resources.Components.RegionRequiredModal.Error));

    ensureUserRegion(data.region, handleCreateLicense, handleRegionError);
  };

  return (
    <LicenseOrderForm
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      isSubmitting={isCreateLicensePending || isSettingRegion}
      hasCarouselEdgeBleed
    />
  );
};
